const SAFE_GAP = 12;
const STABLE_FRAMES = 6;
const STABLE_MS = 96;
const MAX_SETTLE_MS = 1200;

// DOMRects and VisualViewport offsets are in layout-viewport CSS pixels.
// innerHeight/clientHeight are diagnostic only: iOS need not shrink them.
export function measureInjuryForm(dialog, scroller, field, viewport, windowObject = window) {
  const area = scroller.getBoundingClientRect();
  const shell = dialog.getBoundingClientRect();
  const header = dialog.querySelector('.pc-injury-flow__header')?.getBoundingClientRect();
  const block = field.closest('[data-injury-writing-block]') || field.closest('label') || field;
  const completion = block.querySelector('[data-testid="injury-completion"]');
  const top = viewport.offsetTop || 0;
  return {
    visibleTop: Math.max(top, area.top, shell.top, header?.bottom || 0) + SAFE_GAP,
    visibleBottom: Math.min(top + viewport.height, area.bottom, shell.bottom) - SAFE_GAP,
    block: block.getBoundingClientRect(),
    textarea: field.getBoundingClientRect(),
    completion: completion?.getBoundingClientRect(),
    question: completion?.querySelector('legend')?.getBoundingClientRect(),
    scrollTop: scroller.scrollTop, scrollHeight: scroller.scrollHeight, clientHeight: scroller.clientHeight,
    viewport: { height: viewport.height, offsetTop: top, innerHeight: windowObject.innerHeight,
      clientHeight: dialog.ownerDocument.documentElement.clientHeight },
  };
}

export function injuryFormScrollDelta({ visibleTop, visibleBottom, block, textarea, completion, question }) {
  const available = visibleBottom - visibleTop;
  if (available <= 0) return 0;
  // Drop the label first, then the pills, then the question if physically needed.
  const targets = [block,
    { top: textarea.top, bottom: completion?.bottom ?? textarea.bottom },
    { top: textarea.top, bottom: question?.bottom ?? textarea.bottom }, textarea];
  const target = targets.find(box => box.bottom - box.top <= available + 1) || textarea;
  if (target.bottom > visibleBottom + 1) return target.bottom - visibleBottom;
  if (target.top < visibleTop - 1) return target.top - visibleTop;
  return 0;
}

// Only the Injury dialog opts in. Do not share this lifecycle with chat/composers.
export function watchInjuryViewport(dialog, scroller, {
  viewport = window.visualViewport,
  documentObject = document,
  windowObject = window,
  setTimer = setTimeout, clearTimer = clearTimeout,
  requestFrame = callback => windowObject.requestAnimationFrame(callback),
  cancelFrame = id => windowObject.cancelAnimationFrame(id),
  onMeasure = () => {},
} = {}) {
  if (!dialog || !scroller || !viewport) return () => {};
  const geometry = () => ({ height: viewport.height, width: viewport.width,
    top: viewport.offsetTop || 0, scale: viewport.scale || 1 });
  let normal = geometry(), session = null, observation = null, timer = null, frame = null;
  let fieldStyles = null;
  const clearPending = () => {
    if (timer !== null) clearTimer(timer);
    if (frame !== null) cancelFrame(frame);
    timer = null; frame = null; observation = null;
  };
  const restoreField = () => {
    if (fieldStyles) Object.assign(fieldStyles.field.style, fieldStyles.styles);
    fieldStyles = null;
  };
  const cancel = () => { clearPending(); restoreField(); session = null; };
  const isDescription = target => target?.matches?.('#injury-description') && scroller.contains(target);
  const applyGeometry = current => {
    if (Math.abs(current.scale - 1) > .01) return;
    dialog.style.setProperty('--injury-viewport-height', `${current.height}px`);
    dialog.style.setProperty('--injury-viewport-top', `${current.top}px`);
  };
  const setScroll = (top, left = scroller.scrollLeft) => {
    const maximum = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    const target = Math.max(0, Math.min(maximum, top));
    const behavior = scroller.style.scrollBehavior;
    scroller.style.scrollBehavior = 'auto';
    if (Math.abs(scroller.scrollTop - target) > 1) scroller.scrollTop = target;
    if (scroller.scrollLeft !== left) scroller.scrollLeft = left;
    scroller.style.scrollBehavior = behavior;
  };
  const valid = () => session && dialog.isConnected && scroller.contains(session.field);
  const measure = field => measureInjuryForm(dialog, scroller, field, viewport, windowObject);
  const correct = (field, stage) => {
    let measured = measure(field);
    const available = measured.visibleBottom - measured.visibleTop;
    // In an exceptionally short viewport retain the whole input box, with its
    // native internal caret scrolling, rather than hiding half behind the keyboard.
    if (available > 0 && measured.textarea.bottom - measured.textarea.top > available) {
      if (!fieldStyles) {
        const keys = ['minHeight', 'maxHeight', 'boxSizing', 'overflowY'];
        fieldStyles = { field, styles: Object.fromEntries(keys.map(key => [key, field.style[key]])) };
      }
      Object.assign(field.style, { minHeight: '0px', maxHeight: `${available}px`,
        boxSizing: 'border-box', overflowY: 'auto' });
      measured = measure(field);
    }
    const delta = injuryFormScrollDelta(measured);
    const limit = Math.max(0, measured.visibleBottom - measured.visibleTop);
    const bounded = stage === 'verify' ? Math.max(-limit, Math.min(limit, delta)) : delta;
    setScroll(scroller.scrollTop + bounded);
    onMeasure({ stage, ...measured, delta, appliedDelta: scroller.scrollTop - measured.scrollTop });
  };
  const finish = mode => {
    clearPending();
    if (!valid()) { cancel(); return; }
    const saved = session, current = geometry();
    if (Math.abs(current.width - saved.baseline.width) > 2
      || Math.abs(current.scale - saved.baseline.scale) > .01) { cancel(); return; }
    applyGeometry(current);
    if (mode === 'close') {
      if (current.height < saved.baseline.height - 2) return;
      restoreField(); session = null; normal = current;
      setScroll(saved.top, saved.left);
    } else if (documentObject.activeElement === saved.field && !saved.aligned
      && saved.baseline.height - current.height > 120) {
      saved.aligned = true; // Our own scroll/input must not start another pass.
      correct(saved.field, 'initial');
      if (session !== saved || !valid()) return;
      frame = requestFrame(() => {
        frame = null;
        if (session !== saved || !valid() || documentObject.activeElement !== saved.field) return;
        applyGeometry(geometry());
        correct(saved.field, 'verify'); // One measured, bounded residual correction. No loop.
      });
    }
  };
  const snapshot = () => {
    const current = geometry(), area = scroller.getBoundingClientRect();
    const header = dialog.querySelector('.pc-injury-flow__header')?.getBoundingClientRect();
    return [current.height, current.top, current.width, current.scale,
      area.top, area.bottom, header?.bottom || 0, scroller.clientHeight, scroller.scrollTop];
  };
  const sample = time => {
    frame = null;
    if (!valid()) { cancel(); return; }
    applyGeometry(geometry());
    const next = snapshot();
    const same = observation.last?.every((value, i) => Math.abs(value - next[i]) <= .5);
    if (!same) { observation.since = time; observation.frames = 0; }
    observation.last = next;
    observation.frames += 1;
    if (observation.frames >= STABLE_FRAMES && time - observation.since >= STABLE_MS) {
      finish(observation.mode);
    } else frame = requestFrame(sample);
  };
  const observe = mode => {
    if (observation?.mode === mode) return;
    clearPending();
    if (mode === 'close') restoreField();
    observation = { mode, frames: 0, since: 0, last: null };
    // A real maximum, not a debounce restarted by every viewport event.
    timer = setTimer(() => finish(mode), MAX_SETTLE_MS);
    frame = requestFrame(sample);
  };
  const update = () => {
    const current = geometry();
    applyGeometry(current);
    if (!session) { normal = current; return; }
    if (!valid() || Math.abs(current.width - session.baseline.width) > 2
      || Math.abs(current.scale - session.baseline.scale) > .01) {
      cancel(); normal = current; return;
    }
    const open = session.baseline.height - current.height > 120;
    if (open) session.opened = true;
    if (session.opened && current.height >= session.baseline.height - 2) observe('close');
    else {
      if (observation?.mode === 'close') clearPending();
      if (open && !session.aligned && documentObject.activeElement === session.field) observe('open');
    }
  };
  const capture = event => {
    if (!isDescription(event.target) || session) return;
    const current = geometry();
    if (Math.abs(current.scale - 1) > .01 || normal.height - current.height > 120) return;
    normal = current;
    session = { field: event.target, baseline: current, top: scroller.scrollTop,
      left: scroller.scrollLeft, opened: false, aligned: false };
  };
  const focus = event => { capture(event); if (isDescription(event.target)) update(); };
  const blur = () => { clearPending(); if (!session?.opened) session = null; };
  const scroll = () => { if (session?.opened) update(); };
  const rotate = () => { cancel(); normal = geometry(); applyGeometry(normal); };
  applyGeometry(normal);
  scroller.addEventListener('pointerdown', capture, true);
  scroller.addEventListener('focusin', focus);
  scroller.addEventListener('focusout', blur);
  scroller.addEventListener('scroll', scroll);
  viewport.addEventListener('resize', update);
  viewport.addEventListener('scroll', update);
  windowObject.addEventListener('resize', update);
  windowObject.addEventListener('orientationchange', rotate);
  return () => {
    cancel();
    scroller.removeEventListener('pointerdown', capture, true);
    scroller.removeEventListener('focusin', focus);
    scroller.removeEventListener('focusout', blur);
    scroller.removeEventListener('scroll', scroll);
    viewport.removeEventListener('resize', update);
    viewport.removeEventListener('scroll', update);
    windowObject.removeEventListener('resize', update);
    windowObject.removeEventListener('orientationchange', rotate);
    dialog.style.removeProperty('--injury-viewport-height');
    dialog.style.removeProperty('--injury-viewport-top');
  };
}
